import {
  addElmStyle,
  getOptions,
  getStyleSheet,
  useRtl,
  isKeyframeExist,
  getElFontSize,
} from './helper';

interface Options {
  duration?: number | string; // 动画时间
  noRTL?: boolean; // 语序默认从左到右，阿语也不倒序
}

const defaultDuration = 10;

const genRtlKeyframe = (name: string, width: number) => `@keyframes ${name} {
    0%, 20% {
      transform: translate3d(0, 0, 0);
    }
    80%, 100% {
      transform: translate3d(100%, 0, 0) translate3d(-${width}rem, 0, 0);
    }
  }`;
const genNormalKeyframe = (name: string, width: number) => `@keyframes ${name} {
    0%, 20% {
      transform: translate3d(0, 0, 0);
    }
    80%, 100% {
      transform: translate3d(-100%, 0, 0) translate3d(${width}rem, 0, 0);
    }
  }`;

// 生成keyframe入口
const genKeyframe = (isRtl: boolean, options: { width: number; name: string }) =>
  isRtl
    ? genRtlKeyframe(options.name, options.width)
    : genNormalKeyframe(options.name, options.width);

// 容器属性
const useContainer = (el: HTMLElement) => {
  const rootFontSize = getElFontSize(document.documentElement) || 16;
  const containerW100 = Math.ceil((el.clientWidth / Number(rootFontSize)) * 100);
  const containerW = containerW100 / 100;

  return {
    rootFontSize,
    containerW100,
    containerW,
  };
};

// 生成keyframe名
const genKeyName = (options: { width: number; isRtl: boolean }) => {
  const { width, isRtl } = options;
  const prefix = 'bigo-scroll-';
  const suffix = isRtl ? '-rtl' : '';
  return `${prefix}${width}${suffix}`;
};

// 设置styleSheet，将新生成的keyframe插入
const setStyleSheet = (
  el: HTMLElement,
  options: {
    isRtl: boolean;
    keyName: string;
  },
) => {
  const sheetID = 'bigo-scroll-sheet';

  const { isRtl, keyName } = options;
  const { containerW } = useContainer(el);
  const styleSheet = getStyleSheet(sheetID);

  if (isKeyframeExist(sheetID, keyName)) return;

  const newKeyframe = genKeyframe(isRtl, {
    name: keyName,
    width: containerW,
  });
  styleSheet.sheet?.insertRule(newKeyframe);
};

// 标记，防止重复计算
const useFlag = (el: HTMLElement) => {
  const flag = 'data-remWidth';

  const setFlag = (val: string) => el.setAttribute(flag, val);
  const checkHasFlag = () => !!el.getAttribute(flag);

  return {
    setFlag,
    checkHasFlag,
  };
};

function autoScroll(el: HTMLElement, options: Options) {
  const trigger = () => {
    if (!el?.style || !el.children?.[0]) return;

    const child = el.children[0] as HTMLElement;
    const { containerW, containerW100 } = useContainer(el);
    const { isRtl } = useRtl(options);
    const { setFlag } = useFlag(el);
    console.log('autoScroll useContainer', containerW, containerW100);
    if (!containerW) return;

    setFlag(String(containerW)); // 用作标记，记录是否已经有宽度

    // 生成并插入keyFrame
    // animation-name含0.xx的名字会不生效，所以*100
    const keyName = genKeyName({ width: containerW100, isRtl });
    setStyleSheet(el, { isRtl, keyName });

    // 更新el style，应用keyFrame
    const style: Record<string, any> = {
      display: 'inline-block',
      position: 'relative',
      minWidth: `${containerW}rem`,
      animation: `${keyName} ${options.duration || defaultDuration}s infinite linear`,
      willChange: 'transform',
    };
    addElmStyle(child, style, { delay: 50 }); // ios插入keyframe后直接设置css有几率无法触发动画
  };

  // ios首次加载资源时，偶现获取元素宽度有误差，导致样式出错，猜测可能是flexible设置fontsize有延迟导致
  setTimeout(trigger, 50);
}

// 跑马灯指令， 作用在需要滚动显示的节点的父节点上
// 默认取第一个子元素作为滚动元素，故需要保证添加指令的元素内部只有一个子元素
// 保险做法，用一个span包起所有子元素
// 纯文本无法被识别成子元素，用一个span包起来
const vAutoScroll = {
  mounted(el, binding) {
    console.log('vAutoScroll mounted', el, binding);
    const transformStyle = el.style.transform;
    addElmStyle(el, {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      transform: transformStyle ? `${transformStyle} translateZ(0)` : 'translateZ(0)', // 提前告诉浏览器会有动画，提前准备资源，相对的内存会增加
      willChange: 'transform',
    });
    const options = getOptions<Options>(binding.value);
    autoScroll(el, options);
  },
  updated(el, binding) {
    const { checkHasFlag } = useFlag(el);
    if (checkHasFlag()) return;

    // 某些元素，例如弹窗，使用了v-show，会导致一开始获取不到实际宽度，导致展示异常，所以需要在update中进行一次修正
    const options = getOptions<Options>(binding.value);
    autoScroll(el, options);
  },
};

export default vAutoScroll;
